import React, { memo, useCallback, useRef } from 'react';
import {
  PORTRAIT_BREAKPOINT,
  TldrawUiButtonIcon,
  TldrawUiToolbar,
  TldrawUiToolbarButton,
  tlenv,
  unwrapLabel,
  useActions,
  useBreakpoint,
  useLocalStorageState,
  usePassThroughWheelEvents,
  useTldrawUiComponents,
  useTranslation,
} from 'tldraw';

const WHEEL_ZOOM_SHORTCUT = tlenv.isDarwin ? '⌘ + Scroll' : 'Ctrl + Scroll';

/** tldraw navigation zone with wheel-zoom shortcut on +/- tooltips. */
const BoardNavigationPanel = memo(function BoardNavigationPanel() {
  const actions = useActions();
  const msg = useTranslation();
  const breakpoint = useBreakpoint();

  const ref = useRef(null);
  usePassThroughWheelEvents(ref);

  const [collapsed, setCollapsed] = useLocalStorageState('minimap', true);

  const toggleMinimap = useCallback(() => {
    setCollapsed(s => !s);
  }, [setCollapsed]);

  const { ZoomMenu, Minimap } = useTldrawUiComponents();

  if (breakpoint < PORTRAIT_BREAKPOINT.MOBILE) {
    return null;
  }

  const zoomOutLabel = msg(unwrapLabel(actions['zoom-out'].label));
  const zoomInLabel = msg(unwrapLabel(actions['zoom-in'].label));

  return (
    <div ref={ref} className="tlui-navigation-panel">
      <TldrawUiToolbar className="tlui-buttons__horizontal" label={msg('navigation-zone.title')}>
        {ZoomMenu && breakpoint < PORTRAIT_BREAKPOINT.TABLET ? (
          <ZoomMenu />
        ) : (
          <>
            <TldrawUiToolbarButton
              type="icon"
              data-testid="minimap.zoom-out"
              title={`${zoomOutLabel} — ${WHEEL_ZOOM_SHORTCUT}`}
              onClick={() => actions['zoom-out'].onSelect('navigation-zone')}
            >
              <TldrawUiButtonIcon small icon="minus" />
            </TldrawUiToolbarButton>
            {ZoomMenu && <ZoomMenu key="zoom-menu" />}
            <TldrawUiToolbarButton
              type="icon"
              data-testid="minimap.zoom-in"
              title={`${zoomInLabel} — ${WHEEL_ZOOM_SHORTCUT}`}
              onClick={() => actions['zoom-in'].onSelect('navigation-zone')}
            >
              <TldrawUiButtonIcon small icon="plus" />
            </TldrawUiToolbarButton>
            {Minimap && (
              <TldrawUiToolbarButton
                type="icon"
                data-testid="minimap.toggle-button"
                title={msg('navigation-zone.toggle-minimap')}
                onClick={toggleMinimap}
              >
                <TldrawUiButtonIcon small icon={collapsed ? 'chevron-right' : 'chevron-left'} />
              </TldrawUiToolbarButton>
            )}
          </>
        )}
      </TldrawUiToolbar>
      {Minimap && breakpoint >= PORTRAIT_BREAKPOINT.TABLET && !collapsed && <Minimap />}
    </div>
  );
});

export default BoardNavigationPanel;
